// The colors of a job's status, taken from the colors the Jobs table's five `Status` select
// fields carry in Airtable. Those follow one design, and this file keeps it: a hue per phase,
// growing stronger as the phase progresses.
//
//   gray    not started, or a dead end     New, Unready, Submitted, Open, Cancelled
//   cyan    prepared                       Prepared, Ready
//   blue    planned                        Map Made, Scheduled
//   yellow  in the field, a ramp           En Route → Sampling → Sampled
//   green   done, a ramp                   Validated → Shipped → Completed, Data Sent
//   purple  billing, a ramp                Approved → Invoiced
//   red     the roll-up's override state   Error
//
// Airtable's hexes do not come into the portal. Each hue is a theme palette key and each step of
// a ramp a tone the chip draws from that key, which is what makes the chips work in both color
// schemes. Cyan and blue share the theme's `info` (prepared is its soft tone, planned its full
// one); purple is the theme's `billing`, which exists for this.
//
// One flat name → style map serves all five status fields. That is safe because the option names
// are disjoint across the data/logistics/billing tracks (the airtable package asserts this at
// import), and because a name shared with the `Status` roll-up means the same thing there.
//
// The long-standing `status` field ("Job Status - Simple": "1. Ready", "Y. Cancelled") is a
// formula and has no colors of its own. It goes through the same map with its rank prefix
// dropped, so "1. Ready" and the new system's "Ready" are one color wherever both appear. Its two
// names the new system does not have borrow from the nearest one that does.

/** A theme palette key. `default` is the chip's own neutral gray. */
type JobStatusHue = "default" | "info" | "warning" | "success" | "billing" | "error";

/** How far along its phase a status is: a soft tint, a mid tone, the full color. */
type JobStatusTone = "soft" | "mid" | "full";

export interface JobStatusChipStyle {
	hue: JobStatusHue;
	tone: JobStatusTone;
}

const NEUTRAL: JobStatusChipStyle = { hue: "default", tone: "full" };

const STATUS_STYLES: Record<string, JobStatusChipStyle> = {
	// Not started — every chain's first option, plus the roll-up's own idle and dead ends.
	New: NEUTRAL,
	Submitted: NEUTRAL,
	Unready: NEUTRAL,
	Open: NEUTRAL,
	Cancelled: NEUTRAL,

	// Prepared and ready to be worked.
	Prepared: { hue: "info", tone: "soft" },
	Ready: { hue: "info", tone: "soft" },

	// Planned: the job has a map or a place in a shift.
	"Map Made": { hue: "info", tone: "full" },
	Scheduled: { hue: "info", tone: "full" },

	// Out in the field.
	"En Route": { hue: "warning", tone: "soft" },
	Sampling: { hue: "warning", tone: "mid" },
	Sampled: { hue: "warning", tone: "full" },

	// Done, for that chain.
	Validated: { hue: "success", tone: "soft" },
	Shipped: { hue: "success", tone: "mid" },
	Completed: { hue: "success", tone: "full" },
	"Data Sent": { hue: "success", tone: "full" },

	// Billing.
	Approved: { hue: "billing", tone: "soft" },
	Invoiced: { hue: "billing", tone: "full" },

	// The roll-up's override state.
	Error: { hue: "error", tone: "full" },

	// The long-standing field's own two names: shipped to the lab, and costed but not yet invoiced.
	"Sent to Lab": { hue: "success", tone: "mid" },
	"Costs Finalized": { hue: "billing", tone: "soft" },
};

// The rank "Job Status - Simple" puts before each name: a digit, or Y (cancelled) / X (error).
const RANK_PREFIX = /^[0-9XY]\. /;

/**
 * The chip style for a status option, from either status system.
 *
 * Falls back to neutral for an option the base has gained since this map was written, so an
 * unrecognized status still reads as a status rather than as a problem.
 */
export function getJobStatusChipStyle(status: string | null | undefined): JobStatusChipStyle {
	if (!status) return NEUTRAL;
	return STATUS_STYLES[status.replace(RANK_PREFIX, "")] ?? NEUTRAL;
}
